/**
 * Square integration page — Appointments + Retail/Commerce.
 *
 * Auth: Square Access Token from Square Developer Dashboard.
 *   Docs: https://developer.squareup.com/docs/devtools/sandbox/overview
 *
 * Capabilities:
 *   - Connect to a Square merchant account (validate token, discover locations)
 *   - Select which location to use for bookings and retail orders
 *   - Browse bookable services and team members
 *   - AI Skills section — 20 skills across 6 categories
 *   - Data Mapping tab — field reference for workflow variables
 *   - Bot workflows use Availability, Book, Cancel, Catalog, Orders, Customers, Loyalty endpoints
 *
 * Bot API reference:
 *   POST /api/admin/Square/Availability         — find open appointment slots
 *   POST /api/admin/Square/Book                 — create booking
 *   POST /api/admin/Square/Cancel               — cancel booking
 *   POST /api/admin/Square/Catalog/Search       — search product catalog
 *   GET  /api/admin/Square/Catalog/{itemId}     — get catalog item details
 *   GET  /api/admin/Square/Categories           — list categories
 *   POST /api/admin/Square/Inventory/Check      — check stock
 *   POST /api/admin/Square/Orders/Search        — search orders
 *   GET  /api/admin/Square/Orders/{orderId}     — get order details
 *   POST /api/admin/Square/Customer/Search      — search customers by email/phone
 *   POST /api/admin/Square/GiftCard/Balance     — check gift card balance
 */
import * as React from "react";
import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import { Toggle } from "@/components/Toggle";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import SquareDataMapping from "./SquareDataMapping";
import SquareBotKnowledge from "./SquareBotKnowledge";
import IntegrationActivityLog from "../IntegrationActivityLog";
import SkillControlsPanel, { type SkillDef } from "../SkillControlsPanel";
import ShopperProfileTab from "../shared/ShopperProfileTab";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import LaunchWizardButton from "@/components/LaunchWizardButton";

// ─── AI Tools registry ────────────────────────────────────────────────────────

type SquareSkill = SkillDef & { tier: "base" | "retail" };

const AI_TOOLS: SquareSkill[] = [
  // ── Tier 1: Appointments (8 skills — EnableSquare) ────────────────────────
  { tier: "base",   name: "square_check_availability",    label: "Check availability",      group: "Appointments",        description: "Find open appointment slots for a service and optional team member. Returns dates, times, and duration." },
  { tier: "base",   name: "square_book_appointment",      label: "Book appointment",         group: "Appointments",        description: "Create a Square booking for a visitor. Captures name, email, service, and preferred time." },
  { tier: "base",   name: "square_cancel_appointment",    label: "Cancel appointment",       group: "Appointments",        description: "Cancel an existing booking by booking ID. Updates status in Square and Velaro." },
  { tier: "base",   name: "square_reschedule_appointment",label: "Reschedule appointment",   group: "Appointments",        description: "Cancel the existing booking and create a new one at the requested time in a single flow." },
  { tier: "base",   name: "square_list_services",         label: "List services",            group: "Appointments",        description: "Return the full list of bookable services from the Square catalog with duration and price." },
  { tier: "base",   name: "square_get_customer_bookings", label: "Get customer bookings",    group: "Appointments",        description: "Look up a customer's upcoming and past bookings by phone number or customer ID." },
  { tier: "base",   name: "square_get_customer",          label: "Get customer",             group: "Appointments",        description: "Look up a Square customer by phone or email. Returns name, groups, and linked loyalty account." },
  { tier: "base",   name: "square_create_customer",       label: "Create customer",          group: "Appointments",        description: "Create a new Square customer record from visitor data collected in conversation." },
  { tier: "base",   name: "square_list_locations",        label: "List locations",           group: "Appointments",        description: "List all active Square business locations with addresses, phone numbers, and business hours. Use when a customer asks where to go or what the hours are." },

  // ── Tier 2: Retail / Commerce (20 skills — EnableSquareRetail) ───────────
  { tier: "retail", name: "square_search_catalog",        label: "Search catalog",           group: "Catalog & Inventory", description: "Search Square catalog by keyword. Returns matching items with name, price, and variations." },
  { tier: "retail", name: "square_get_catalog_item",      label: "Get catalog item",         group: "Catalog & Inventory", description: "Fetch full item details: name, description, price variations, SKU, images, and taxes." },
  { tier: "retail", name: "square_list_categories",       label: "List categories",          group: "Catalog & Inventory", description: "Return all product/service categories from the Square catalog." },
  { tier: "retail", name: "square_check_inventory",       label: "Check inventory",          group: "Catalog & Inventory", description: "Check current stock count for a catalog item at the configured location." },
  { tier: "retail", name: "square_search_orders",         label: "Search orders",            group: "Orders",              description: "Search a customer's orders by customer ID, date range, or order state." },
  { tier: "retail", name: "square_get_order",             label: "Get order details",        group: "Orders",              description: "Retrieve full order details: line items, totals, fulfillment status, and timestamps." },
  { tier: "retail", name: "square_cancel_order",          label: "Cancel order",             group: "Orders",              description: "Request order cancellation and surface cancellation policy to the visitor." },
  { tier: "retail", name: "square_update_customer",       label: "Update customer",          group: "Orders",              description: "Update customer profile fields such as email, phone, birthday, or reference ID." },
  { tier: "retail", name: "square_get_loyalty_balance",   label: "Get loyalty balance",      group: "Loyalty & Gifts",     description: "Return a customer's current point balance and lifetime points from their loyalty account." },
  { tier: "retail", name: "square_get_loyalty_rewards",   label: "Get loyalty rewards",      group: "Loyalty & Gifts",     description: "List available loyalty rewards that the customer can redeem based on their point balance." },
  { tier: "retail", name: "square_check_gift_card",       label: "Check gift card",          group: "Loyalty & Gifts",     description: "Look up a gift card by GAN (Gift Account Number) and return the current balance." },
  { tier: "retail", name: "square_get_payment_history",   label: "Get payment history",      group: "Payments",            description: "Retrieve recent payment history for a customer. Returns amount, date, and payment method." },
  { tier: "retail", name: "square_create_checkout_link",  label: "Create checkout link",     group: "Payments",            description: "Create a Square payment link to share in chat. The customer clicks it to pay securely online.", write: true, channelWarning: "Sends a payment URL — won't work on IVR." },
  { tier: "retail", name: "square_create_refund",         label: "Create refund",            group: "Payments",            description: "Issue a full or partial refund on a completed Square payment. Always confirms with the customer before executing.", write: true, destructive: true },
  { tier: "retail", name: "square_list_invoices",         label: "List invoices",            group: "Invoices",            description: "List up to 10 recent Square invoices for a customer. Returns status, title, due dates, and amounts owed." },
  { tier: "retail", name: "square_get_invoice",           label: "Get invoice",              group: "Invoices",            description: "Get full invoice details including payment requests, due dates, and a public URL the customer can use to pay." },
  { tier: "retail", name: "square_get_subscription",      label: "Get subscription",         group: "Customers",           description: "Look up a Square subscription by ID or customer. Returns plan status, billing dates, and renewal info — useful for membership businesses." },
  { tier: "retail", name: "square_get_customer_segments", label: "Get customer segments",    group: "Customers",           description: "Get which segments a customer belongs to (e.g. High Spenders, First Time Buyers), or list all segment definitions." },
  { tier: "retail", name: "square_redeem_loyalty_reward", label: "Redeem loyalty reward",    group: "Loyalty & Gifts",     description: "Redeem a Square loyalty reward for a customer. Always confirms before executing — this action is irreversible.", write: true },
];

const AI_TOOL_GROUPS = Array.from(new Set(AI_TOOLS.map((t) => t.group)));

// ─── Types ────────────────────────────────────────────────────────────────────

interface SquareStatus {
  configured: boolean;
  displayName: string;
  merchantName: string;
  locationId: string;
  locationName: string;
  isSandbox: boolean;
  isActive?: boolean;
  disabledAt?: string | null;
  disabledBy?: string | null;
}

interface SquareLocation {
  id: string;
  name: string;
  address: string;
}

interface SquareService {
  id: string;
  name: string;
  durationMinutes: number | null;
}

interface SquareTeamMember {
  id: string;
  name: string;
}

type ActiveTab = "configuration" | "ai-skills" | "bot-knowledge" | "data-mapping" | "logs" | "shoppers";

// ─── Main component ───────────────────────────────────────────────────────────

export default function SquarePage() {
  const api = useApi();

  const [status, setStatus] = useState<SquareStatus>({
    configured: false,
    displayName: "",
    merchantName: "",
    locationId: "",
    locationName: "",
    isSandbox: false
  });
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState<ActiveTab>("configuration");
  const { enableEcommerceSync } = useAdminFeatures();

  // Form state
  const [token, setToken]             = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isSandbox, setIsSandbox]     = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveError, setSaveError]     = useState<string | null>(null);

  // Location picker
  const [locations, setLocations]                       = useState<SquareLocation[]>([]);
  const [selectedLocationId, setSelectedLocationId]     = useState("");
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [loadingLocations, setLoadingLocations]         = useState(false);

  // Services + team members
  const [services, setServices]             = useState<SquareService[]>([]);
  const [teamMembers, setTeamMembers]       = useState<SquareTeamMember[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [disconnecting, setDisconnecting] = useState(false);
  const [showWizard, setShowWizard]       = useState(false);
  const [wizardIntegration, setWizardIntegration] = useState<"square-appointments" | "square">("square-appointments");

  // Missed call text-back
  interface MissedCallConfig {
    configured: boolean;
    hasToken: boolean;
    webhookUrl: string | null;
    fromNumber: string | null;
    smsTemplate: string | null;
  }
  const [missedCall, setMissedCall]         = useState<MissedCallConfig>({ configured: false, hasToken: false, webhookUrl: null, fromNumber: null, smsTemplate: null });
  const [mcFromNumber, setMcFromNumber]     = useState("");
  const [mcSmsTemplate, setMcSmsTemplate]   = useState("");
  const [mcSaving, setMcSaving]             = useState(false);
  const [mcRegenerating, setMcRegenerating] = useState(false);
  const [mcSaved, setMcSaved]               = useState(false);

  // OAuth connect flow
  const [oauthLoading, setOauthLoading] = useState(false);
  const [usePat, setUsePat]             = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await api.admin.get("Square/Status");
      const data = await resp.json();
      if (data && typeof data === "object") {
        setStatus(data);
        if (data.configured) {
          setSelectedLocationId(data.locationId ?? "");
          setSelectedLocationName(data.locationName ?? "");
          setDisplayName(data.displayName ?? "");
          setIsSandbox(data.isSandbox ?? false);
        }
      }
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const fetchLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const resp = await api.admin.get("Square/Locations");
      const data = await resp.json();
      setLocations(Array.isArray(data) ? data : []);
    } catch {
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, [api.admin]);

  const fetchDetails = useCallback(async () => {
    setLoadingDetails(true);
    try {
      const [svcResp, tmResp] = await Promise.all([
        api.admin.get("Square/Services"),
        api.admin.get("Square/TeamMembers")
      ]);
      const svcData = await svcResp.json();
      const tmData  = await tmResp.json();
      setServices(Array.isArray(svcData) ? svcData : []);
      setTeamMembers(Array.isArray(tmData) ? tmData : []);
    } catch {
      // non-critical
    } finally {
      setLoadingDetails(false);
    }
  }, [api.admin]);

  useEffect(() => {
    if (status.configured) {
      fetchDetails();
      fetchMissedCallConfig();
    }
  }, [status.configured, fetchDetails]);

  const fetchMissedCallConfig = useCallback(async () => {
    try {
      const resp = await api.admin.get("Square/MissedCallConfig");
      const data = await resp.json();
      if (data && typeof data === "object") {
        setMissedCall(data);
        setMcFromNumber(data.fromNumber ?? "");
        setMcSmsTemplate(data.smsTemplate ?? "Hi! We missed your call. Text back to book your appointment with {{merchantName}}.");
      }
    } catch {
      // non-critical
    }
  }, [api.admin]);

  async function handleFetchLocations() {
    if (!token) {
      setSaveError("Please enter your Square Access Token first, then click Fetch Locations.");
      return;
    }
    setSaveError(null);
    setLoadingLocations(true);
    try {
      const resp = await api.admin.post("Square/Config", {
        displayName: displayName || "My Square Account",
        accessToken: token,
        locationId: "",
        locationName: "",
        isSandbox
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setSaveError(data?.error ?? "Invalid Square access token. Please check it and try again.");
        return;
      }
      await fetchLocations();
      await fetchStatus();
    } catch {
      setSaveError("Failed to fetch locations. Please check your token.");
    } finally {
      setLoadingLocations(false);
    }
  }

  async function handleSave() {
    if (!token && !status.configured) {
      setSaveError("Please enter your Square Access Token.");
      return;
    }
    if (!selectedLocationId) {
      setSaveError("Please select a location for bookings.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const resp = await api.admin.post("Square/Config", {
        displayName: displayName || "My Square Account",
        accessToken: token || undefined,
        locationId: selectedLocationId,
        locationName: selectedLocationName,
        isSandbox
      });
      const data = await resp.json();
      if (!resp.ok) {
        setSaveError(data?.error ?? "Failed to save configuration.");
        return;
      }
      await fetchStatus();
      await fetchDetails();
      setToken("");
    } catch {
      setSaveError("An unexpected error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveMissedCall() {
    setMcSaving(true);
    setMcSaved(false);
    try {
      await api.admin.post("Square/MissedCallConfig", { fromNumber: mcFromNumber, smsTemplate: mcSmsTemplate });
      await fetchMissedCallConfig();
      setMcSaved(true);
      setTimeout(() => setMcSaved(false), 3000);
    } catch {
      // ignore
    } finally {
      setMcSaving(false);
    }
  }

  async function handleRegenerateToken() {
    if (!confirm("Generate a new webhook URL? The old URL will stop working immediately — update your phone system settings.")) return;
    setMcRegenerating(true);
    try {
      await api.admin.post("Square/MissedCallToken/Regenerate", {});
      await fetchMissedCallConfig();
    } catch {
      // ignore
    } finally {
      setMcRegenerating(false);
    }
  }

  async function handleOAuthConnect() {
    setOauthLoading(true);
    setSaveError(null);
    try {
      const resp = await api.admin.get("Square/OAuth/AuthUrl");
      const data = await resp.json();
      if (!resp.ok) {
        setUsePat(true);
        setSaveError(data?.error ?? null);
        return;
      }
      window.location.href = data.authUrl;
    } catch {
      setUsePat(true);
      setSaveError("Could not start Square OAuth. Enter your Access Token manually below.");
    } finally {
      setOauthLoading(false);
    }
  }

  async function handleToggleActive() {
    try {
      const resp = await api.admin.post("Square/toggle-active", {});
      const data = await resp.json();
      setStatus((prev) => ({
        ...prev,
        isActive: data.isActive ?? true,
        disabledAt: data.disabledAt ?? null,
        disabledBy: data.disabledBy ?? null,
      }));
    } catch {
      // non-critical
    }
  }

  async function handleDisconnect() {
    if (!confirm("Disconnect Square? All bot workflows that rely on Square will stop working.")) return;
    setDisconnecting(true);
    try {
      await api.admin.delete("Square/Config");
      setStatus({ configured: false, displayName: "", merchantName: "", locationId: "", locationName: "", isSandbox: false });
      setToken("");
      setLocations([]);
      setServices([]);
      setTeamMembers([]);
      setSelectedLocationId("");
      setSelectedLocationName("");
    } catch {
      // ignore
    } finally {
      setDisconnecting(false);
    }
  }

  if (loading) {
    return <div className="p-8 text-slate-500">Loading Square configuration…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 space-y-6">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-sm">S</div>
          <div>
            <h1 className="text-xl font-semibold text-slate-800">Square</h1>
            <p className="text-sm text-slate-500">
              Appointments, retail catalog, orders, inventory, loyalty, and gift cards — all accessible to your AI bots via 20 skills.
            </p>
          </div>
        </div>
        {status.configured && (
          <div className="flex gap-2 shrink-0">
            <LaunchWizardButton onClick={() => { setWizardIntegration("square-appointments"); setShowWizard(true); }} />
          </div>
        )}
      </div>

      {/* ── Connection status banner ── */}
      {status.configured && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-green-800 font-medium">Connected to Square</p>
            <p className="text-green-700 text-sm">{status.merchantName} — {status.locationName || "No location selected"}{status.isSandbox && " (Sandbox)"}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleToggleActive}
              className={`text-xs px-3 py-1 rounded-full font-medium ${
                (status.isActive ?? true)
                  ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  : "bg-amber-100 text-amber-700 hover:bg-amber-200"
              }`}
              title={(status.isActive ?? true) ? "Click to pause integration" : "Click to resume integration"}
            >
              {(status.isActive ?? true) ? "Active" : "Paused"}
            </button>
            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="text-red-600 text-sm hover:underline disabled:opacity-50"
            >
              {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
          </div>
        </div>
      )}

      {/* ── Disabled banner ── */}
      {status.configured && !(status.isActive ?? true) && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3">
          <i className="fa-solid fa-triangle-exclamation text-amber-500 mt-0.5" />
          <div className="text-sm text-amber-800">
            <strong>Integration paused</strong>
            {status.disabledAt && (
              <span> on {new Date(status.disabledAt).toLocaleDateString()}</span>
            )}
            {status.disabledBy && (
              <span> by {status.disabledBy}</span>
            )}
            {" "}— your bot will not use Square data while paused.
          </div>
        </div>
      )}

      {/* ── Tab navigation ── */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-0" aria-label="Square configuration tabs">
          {([
            { id: "configuration", label: "Configuration",   icon: "fa-gear" },
            { id: "ai-skills",     label: "AI Skills",       icon: "fa-robot",   badge: "28" },
            { id: "bot-knowledge", label: "Bot Knowledge",   icon: "fa-graduation-cap" },
            { id: "data-mapping",  label: "Data Mapping",    icon: "fa-table" },
            { id: "logs",          label: "Activity Log",    icon: "fa-list" },
            ...(enableEcommerceSync ? [{ id: "shoppers" as ActiveTab, label: "Shoppers", icon: "fa-users" }] : []),
          ] as { id: ActiveTab; label: string; icon: string; badge?: string }[]).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-black text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              <i className={`fa-solid ${tab.icon} text-xs`} />
              {tab.label}
              {tab.badge && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{tab.badge}</span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {status.configured && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 mt-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Square integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window" className="text-xs px-3 py-1.5 rounded-lg bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 transition">Chat Window</Link>
              <Link to="/deployments" className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition">Get Embed Code →</Link>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <i className="fa-solid fa-lightbulb mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <Link to="/deployments" className="underline hover:text-blue-900">Deployments → Page Widgets</Link> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {/* ── Configuration tab ── */}
      {activeTab === "configuration" && (
        <div className="space-y-6">
          {/* Configuration form */}
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
            <h2 className="text-base font-semibold text-slate-700">
              {status.configured ? "Update Configuration" : "Connect Square"}
            </h2>

            {/* One-click OAuth button */}
            {!status.configured && !usePat && (
              <div className="space-y-3">
                <button
                  onClick={handleOAuthConnect}
                  disabled={oauthLoading}
                  className="w-full flex items-center justify-center gap-3 bg-black text-white rounded-lg px-4 py-3 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
                >
                  <span className="text-base font-bold">S</span>
                  {oauthLoading ? "Redirecting to Square…" : "Connect with Square"}
                </button>
                <p className="text-center text-xs text-slate-400">
                  You'll be redirected to Square to authorize Velaro.{" "}
                  <button
                    type="button"
                    className="text-blue-500 underline"
                    onClick={() => setUsePat(true)}
                  >
                    Use an access token instead
                  </button>
                </p>
              </div>
            )}

            {/* Manual PAT instructions */}
            {(status.configured || usePat) && (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 space-y-2">
                <p className="font-medium">How to get your Square Access Token:</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Go to <strong>developer.squareup.com</strong> and sign in</li>
                  <li>Open your application (or create one) and click <strong>Credentials</strong></li>
                  <li>
                    Copy the <strong>Access Token</strong> — for sandbox, use the <strong>Sandbox Access Token</strong>.
                    <br />
                    <span className="text-blue-700">It starts with <code className="bg-blue-100 px-1 rounded">EAAAl</code> and is a long string of letters and numbers.</span>
                  </li>
                  <li>Paste it below and click <strong>Fetch Locations</strong></li>
                </ol>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800 text-xs">
                  <strong>Do not use the Application ID.</strong> The Application ID (shown above the Access Token in the dashboard, starts with <code className="bg-amber-100 px-0.5 rounded">sq0idb-</code> or <code className="bg-amber-100 px-0.5 rounded">sandbox-sq0idb-</code>) is not an access token and will not work here. Only the <strong>Access Token</strong> field is needed.
                </div>
              </div>
            )}

            {(status.configured || usePat) && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Display name</label>
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. Downtown Salon"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Square Access Token {status.configured && <span className="text-slate-400 font-normal">(leave blank to keep existing)</span>}
                  </label>
                  <input
                    type="password"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={status.configured ? "••••••••••••••••" : "EAAAlxxxxxxx…"}
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    autoComplete="off"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Toggle
                    toggled={isSandbox}
                    changeToggle={() => setIsSandbox(!isSandbox)}
                    label="Sandbox mode (for testing)"
                  />
                  <span className="text-xs text-slate-500">Use sandbox tokens from Square Developer Dashboard</span>
                </div>

                <div>
                  <SecondaryBlueButton
                    label={loadingLocations ? "Fetching…" : "Fetch Locations"}
                    onClick={handleFetchLocations}
                    disabled={loadingLocations}
                  />
                  <p className="text-xs text-slate-500 mt-1">Validates your token and discovers your Square locations.</p>
                </div>
              </>
            )}

            {/* Location picker */}
            {locations.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location for bookings and orders</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={selectedLocationId}
                  onChange={(e) => {
                    const loc = locations.find((l) => l.id === e.target.value);
                    setSelectedLocationId(e.target.value);
                    setSelectedLocationName(loc?.name ?? "");
                  }}
                >
                  <option value="">Select a location…</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name}{loc.address ? ` — ${loc.address}` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {saveError && <p className="text-sm text-red-600">{saveError}</p>}

            {(status.configured || usePat) && (
              <div className="flex gap-3 pt-2">
                <PrimaryBlueButton
                  label={saving ? "Saving…" : "Save Configuration"}
                  onClick={handleSave}
                  disabled={saving}
                />
              </div>
            )}
          </div>

          {/* Services & Team Members */}
          {status.configured && (
            <>
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="text-base font-semibold text-slate-700 mb-4">Bookable Services</h2>
                {loadingDetails ? (
                  <p className="text-sm text-slate-500">Loading services…</p>
                ) : services.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No appointment services found. Make sure your Square Catalog has items with type <strong>Appointments service</strong>.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 pr-4 font-medium text-slate-600">Service</th>
                          <th className="text-left py-2 font-medium text-slate-600">Duration</th>
                          <th className="text-left py-2 font-medium text-slate-600">Service ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((svc) => (
                          <tr key={svc.id} className="border-b border-slate-100">
                            <td className="py-2 pr-4 text-slate-800">{svc.name}</td>
                            <td className="py-2 text-slate-600">{svc.durationMinutes ? `${svc.durationMinutes} min` : "—"}</td>
                            <td className="py-2">
                              <code className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{svc.id}</code>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h2 className="text-base font-semibold text-slate-700 mb-4">Staff (Team Members)</h2>
                {loadingDetails ? (
                  <p className="text-sm text-slate-500">Loading team members…</p>
                ) : teamMembers.length === 0 ? (
                  <p className="text-sm text-slate-500">No active team members found at this location.</p>
                ) : (
                  <div className="space-y-2">
                    {teamMembers.map((tm) => (
                      <div key={tm.id} className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <span className="text-slate-800">{tm.name}</span>
                        <code className="text-xs bg-slate-100 rounded px-1.5 py-0.5 text-slate-700">{tm.id}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bot API Reference */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
                <h2 className="text-base font-semibold text-slate-700">Bot API Reference</h2>
                <p className="text-sm text-slate-500">
                  Use these endpoints in <strong>HTTP Request</strong> nodes in your bot workflows.
                  All calls require the admin API key in the Authorization header.
                </p>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Appointments</p>
                    <pre className="bg-slate-800 text-green-300 text-xs rounded-lg p-3 overflow-x-auto">{`POST /api/admin/Square/Availability  — find open slots
POST /api/admin/Square/Book           — create booking
POST /api/admin/Square/Cancel         — cancel booking`}</pre>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Retail / Commerce</p>
                    <pre className="bg-slate-800 text-green-300 text-xs rounded-lg p-3 overflow-x-auto">{`POST /api/admin/Square/Catalog/Search     — search catalog by keyword
GET  /api/admin/Square/Catalog/{itemId}  — get catalog item details
GET  /api/admin/Square/Categories        — list categories
POST /api/admin/Square/Inventory/Check   — check stock
POST /api/admin/Square/Orders/Search     — search orders
GET  /api/admin/Square/Orders/{orderId} — get order details
POST /api/admin/Square/Customer/Search   — search customers
POST /api/admin/Square/GiftCard/Balance  — check gift card balance`}</pre>
                  </div>
                </div>
              </div>

              {/* Missed Call Text-Back */}
              <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
                <div>
                  <h2 className="text-base font-semibold text-slate-700">Missed Call Text-Back</h2>
                  <p className="text-sm text-slate-500 mt-1">
                    When a call to your business goes unanswered, your phone system POSTs to this webhook and Velaro
                    automatically texts the caller with a booking prompt. Works with SkySwitch, RingCentral, Zoom Phone,
                    Vonage, 8x8, Nextiva, Twilio Studio, and any phone system that supports webhooks.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Webhook URL</label>
                  {missedCall.hasToken && missedCall.webhookUrl ? (
                    <div className="flex gap-2 items-center">
                      <input
                        readOnly
                        className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm bg-slate-50 text-slate-700 font-mono"
                        value={missedCall.webhookUrl}
                      />
                      <button
                        className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                        onClick={() => navigator.clipboard.writeText(missedCall.webhookUrl ?? "")}
                      >
                        Copy
                      </button>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No webhook URL yet — click Generate below.</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    Paste this URL into your phone system's "missed call webhook" setting.
                    POST body must include the caller's phone in:{" "}
                    <code className="bg-slate-100 rounded px-1">From</code>,{" "}
                    <code className="bg-slate-100 rounded px-1">caller</code>, or{" "}
                    <code className="bg-slate-100 rounded px-1">phone</code>
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleRegenerateToken}
                    disabled={mcRegenerating}
                    className="text-sm border border-slate-300 rounded-lg px-4 py-2 hover:bg-slate-50 disabled:opacity-50"
                  >
                    {mcRegenerating ? "Generating…" : missedCall.hasToken ? "Regenerate URL" : "Generate Webhook URL"}
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Twilio From Number <span className="text-slate-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+15551234567"
                    value={mcFromNumber}
                    onChange={(e) => setMcFromNumber(e.target.value)}
                  />
                  <p className="text-xs text-slate-400 mt-1">The Twilio number that sends the SMS. E.164 format.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">SMS Message</label>
                  <textarea
                    rows={2}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={mcSmsTemplate}
                    onChange={(e) => setMcSmsTemplate(e.target.value)}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Use <code className="bg-slate-100 rounded px-1">{"{{merchantName}}"}</code> to insert your business name.
                    Current length: <strong>{mcSmsTemplate.length}</strong> / 160 chars
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <PrimaryBlueButton
                    label={mcSaving ? "Saving…" : "Save"}
                    onClick={handleSaveMissedCall}
                    disabled={mcSaving}
                  />
                  {mcSaved && <span className="text-sm text-green-600">Saved!</span>}
                </div>
              </div>
            </>
          )}

          {/* Wizard CTA */}
          {status.configured && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-700">Ready to build your Square bot?</p>
                <p className="text-xs text-slate-500 mt-0.5">Launch the wizard to create a booking or retail assistant workflow in 60 seconds.</p>
              </div>
              <div className="flex gap-2">
                <SecondaryBlueButton
                  label="Booking Wizard"
                  onClick={() => { setWizardIntegration("square-appointments"); setShowWizard(true); }}
                />
                <PrimaryBlueButton
                  label="Retail Wizard"
                  onClick={() => { setWizardIntegration("square"); setShowWizard(true); }}
                />
              </div>
            </div>
          )}

          {/* How it works */}
          <details className="bg-white border border-slate-200 rounded-xl">
            <summary className="px-6 py-4 text-base font-semibold text-slate-700 cursor-pointer">
              How it works
            </summary>
            <div className="px-6 pb-6 text-sm text-slate-600 space-y-3">
              <p>
                Square is the commerce platform used by salons, spas, fitness studios, boutiques, and retail shops.
                Velaro adds a <strong>conversational front door</strong>: visitors book appointments, look up orders,
                check loyalty points, and browse your catalog — all via SMS, WhatsApp, or website chat.
              </p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Connect Square with one click (OAuth) — no manual token handling</li>
                <li>AI skills activate automatically: 20 tools across appointments, catalog, orders, customers, loyalty, and payments</li>
                <li>Bot workflows call live Square APIs at conversation time — always up-to-date data</li>
                <li>Square handles its own reminders, payment collection, and loyalty point tracking</li>
                <li>Missed calls → auto-SMS → booking link; zero manual follow-up required</li>
              </ol>
            </div>
          </details>
        </div>
      )}

      {/* ── AI Skills tab ── */}
      {activeTab === "ai-skills" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-base font-semibold text-slate-800">Square AI Skills</h2>
                <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-900 text-white">20 tools</span>
                {status.configured && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
                )}
              </div>
              <p className="text-sm text-slate-500">
                Available to any AI workflow node on this site once Square is connected.
                No skill-level configuration required — the AI decides when to call Square based on conversation context.
              </p>
            </div>
            {status.configured && (
              <div className="flex gap-2 shrink-0">
                <SecondaryBlueButton
                  label="Booking Wizard"
                  onClick={() => { setWizardIntegration("square-appointments"); setShowWizard(true); }}
                />
                <PrimaryBlueButton
                  label="Retail Wizard"
                  onClick={() => { setWizardIntegration("square"); setShowWizard(true); }}
                />
              </div>
            )}
          </div>

          {/* Not connected warning */}
          {!status.configured && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              <strong>Square is not connected.</strong> Connect Square on the Configuration tab to activate these skills.
            </div>
          )}

          {/* Two-tier skill breakdown */}
          <div className="border-t border-slate-100 pt-4 space-y-6">
            {/* Tier 1 — Appointments */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Tier 1 — Appointments</span>
                <span className="text-[10px] text-slate-400">8 skills · EnableSquare</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AI_TOOLS.filter((t) => t.tier === "base").map((tool) => (
                  <div key={tool.name} className={`flex gap-3 p-3 rounded-lg border ${status.configured ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
                    <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${status.configured ? "bg-sky-500" : "bg-slate-300"}`}>
                      {status.configured && (
                        <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                          <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                      <div className="text-xs text-slate-500 mt-0.5 leading-snug">{tool.description}</div>
                      <code className="text-[10px] text-slate-400 mt-1 block truncate">{tool.name}</code>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tier 2 — Retail / Commerce */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Tier 2 — Retail / Commerce</span>
                <span className="text-[10px] text-slate-400">{AI_TOOLS.filter(t => t.tier === "retail").length} skills · EnableSquareRetail</span>
              </div>
              {/* Group retail skills by category — derived from AI_TOOL_GROUPS so new groups render automatically */}
              {AI_TOOL_GROUPS.filter((g) => AI_TOOLS.some((t) => t.tier === "retail" && t.group === g)).map((group) => {
                const tools = AI_TOOLS.filter((t) => t.tier === "retail" && t.group === group);
                if (!tools.length) return null;
                return (
                  <div key={group} className="mb-3">
                    <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">{group}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {tools.map((tool) => (
                        <div key={tool.name} className={`flex gap-3 p-3 rounded-lg border ${status.configured ? "border-violet-200 bg-violet-50" : "border-slate-200 bg-slate-50"}`}>
                          <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${status.configured ? "bg-violet-500" : "bg-slate-300"}`}>
                            {status.configured && (
                              <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                                <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-700">{tool.label}</div>
                            <div className="text-xs text-slate-500 mt-0.5 leading-snug">{tool.description}</div>
                            <code className="text-[10px] text-slate-400 mt-1 block truncate">{tool.name}</code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Usage guide */}
          <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
            <h3 className="text-xs font-semibold text-slate-700 mb-2">How to use Square skills in a workflow</h3>
            <ol className="text-xs text-slate-600 space-y-1 list-decimal ml-4">
              <li>Add an <strong>AI Node</strong> to your workflow (Bots → Edit → add node)</li>
              <li>Select your AI Config (or create one)</li>
              <li>In the system prompt, instruct the AI which Square actions to take</li>
              <li>The AI automatically calls the right Square skill based on visitor input</li>
              <li>Use the <strong>Starter Wizard</strong> above for pre-built templates</li>
            </ol>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <SkillControlsPanel integration="square" skills={AI_TOOLS} />
          </div>
        </div>
      )}

      {/* ── Bot Knowledge tab ── */}
      {activeTab === "bot-knowledge" && (
        <div className="space-y-4">
          <SquareBotKnowledge />
        </div>
      )}

      {/* ── Data Mapping tab ── */}
      {activeTab === "data-mapping" && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-slate-800">Square Data Field Reference</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Reference guide for all available Square data fields and their workflow variable paths.
            </p>
          </div>
          <SquareDataMapping />
        </div>
      )}

      {/* ── Activity Log tab ── */}
      {activeTab === "logs" && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <IntegrationActivityLog integrationSlug="Square" />
        </div>
      )}

      {/* ── Shoppers tab (Ecommerce Sync) ── */}
      {activeTab === "shoppers" && enableEcommerceSync && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="font-semibold text-slate-800 text-lg mb-1">Shopper Profiles — Square</div>
          <p className="text-sm text-slate-500 mb-4">All customers synced from your Square account. Data updates hourly.</p>
          <ShopperProfileTab platform="Square" />
        </div>
      )}

      {/* ── Wizard ── */}
      {showWizard && (
        <StarterWorkflowWizard
          integration={wizardIntegration}
          onClose={() => setShowWizard(false)}
        />
      )}
    </div>
  );
}
